import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { segnaTappa } from "@/components/VisitaOspite";
import axios from "axios";
import { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, ExternalLink, AlertTriangle, MapPin, Building2, Calendar } from "lucide-react";

const STATUS_LABEL = { attivo: "Attivo", sospeso: "Sospeso", chiuso: "Chiuso" };

export default function CantierePubblico() {
  const { id } = useParams();

  // Se a leggere questo QR e' un ospite in visita, la tappa entra nel suo
  // giro. Per tutti gli altri non succede niente.
  useEffect(() => { segnaTappa("cantiere", id); }, [id]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/sites/${id}`).then(r => setData(r.data))
      .catch(() => setError("Cantiere non trovato"));
  }, [id]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card><CardContent className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h2 className="font-display text-2xl font-bold">{error}</h2>
      </CardContent></Card>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Caricamento...</div>;

  const { company, site, documents } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 bg-warning-stripe" />
        <div className="max-w-3xl mx-auto px-6 py-8 relative">
          <div className="flex items-start gap-4 flex-wrap">
            <img src={company.logo_url} alt={company.name} className="h-14 w-auto bg-white p-1 rounded" />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.3em] text-slate-400">Scheda cantiere</div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">{site.name}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-300">
                {site.status && <Badge className="bg-orange-500 text-white border-0">{STATUS_LABEL[site.status] || site.status}</Badge>}
                {site.activity_type && <span>{site.activity_type}</span>}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-500" /> Società esecutrice
            </h2>
            <div className="text-sm text-slate-700">
              <div className="font-semibold">{company.name}</div>
              <div className="text-slate-500 flex items-center gap-1 mt-1">
                <MapPin className="w-3 h-3" />{company.address}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-bold mb-4">Informazioni cantiere</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500 block text-xs uppercase tracking-wide">Indirizzo cantiere</span>
                <span className="font-semibold">{site.address}</span>
              </div>
              {site.client && (
                <div>
                  <span className="text-slate-500 block text-xs uppercase tracking-wide">Committente</span>
                  <span className="font-semibold">{site.client}</span>
                </div>
              )}
              {site.activity_type && (
                <div>
                  <span className="text-slate-500 block text-xs uppercase tracking-wide">Tipologia opere</span>
                  <span className="font-semibold">{site.activity_type}</span>
                </div>
              )}
              {(site.start_date || site.end_date_planned) && (
                <div>
                  <span className="text-slate-500 block text-xs uppercase tracking-wide flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Periodo lavori
                  </span>
                  <span className="font-semibold">
                    {site.start_date?.substring(0, 10) || "—"} → {site.end_date_planned?.substring(0, 10) || "in corso"}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h2 className="font-display text-xl font-bold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" /> Documenti disponibili
            </h2>
            {documents.length === 0
              ? <p className="text-sm text-slate-500">Nessun documento pubblico disponibile per questo cantiere.</p>
              : <div className="space-y-2">
                  {documents.map(d => (
                    <div key={d.id} className="p-2 rounded border border-emerald-200 bg-emerald-50 flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{d.title}</div>
                        <div className="text-xs text-slate-500">{d.doc_type}</div>
                      </div>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api${d.file_url}`} target="_blank" rel="noopener noreferrer"
                         className="text-orange-600 flex-shrink-0">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 py-4">
          {company.name} · Scheda generata automaticamente
        </p>
      </main>
    </div>
  );
}
